/**
 * Ardyss Live: shared behaviour for the live shopping landing.
 *
 * The banner section (live-shopping-hero) prints the schedule as JSON in
 * `script[data-live-schedule]`. From it this script:
 * - works out the status (upcoming / live / ended, or a manual override) and
 *   writes it to <html data-live-status>, so any section can show or hide
 *   content with [data-live-show="upcoming live ended"];
 * - runs the countdown, prints the date in the visitor's own time zone and
 *   builds the "add to calendar" links (Google Calendar and .ics);
 * - loads stream players only when the visitor presses play.
 */
(() => {
  if (window.ardyssLiveLoaded) return;
  window.ardyssLiveLoaded = true;

  const root = document.documentElement;

  /* ---------------- Schedule ---------------- */

  /**
   * Offset (ms) between UTC and the wall clock of a time zone at a given instant.
   * @param {number} utcMs
   * @param {string} timeZone
   */
  const zoneOffset = (utcMs, timeZone) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(new Date(utcMs));
    /** @type {Record<string, number>} */
    const p = {};
    for (const part of parts) if (part.type !== 'literal') p[part.type] = Number(part.value);
    return Date.UTC(p.year, p.month - 1, p.day, p.hour % 24, p.minute, p.second) - utcMs;
  };

  /**
   * "2026-11-05" + "19:00" in "America/Chicago" -> UTC timestamp (handles DST).
   * @param {string} date
   * @param {string} time
   * @param {string} timeZone
   */
  const zonedToUtc = (date, time, timeZone) => {
    const [y, m, d] = date.split('-').map(Number);
    const [hh, mm] = (time || '00:00').split(':').map(Number);
    if (![y, m, d, hh, mm].every(Number.isFinite)) return null;
    const wall = Date.UTC(y, m - 1, d, hh, mm);
    try {
      const first = zoneOffset(wall, timeZone);
      const second = zoneOffset(wall - first, timeZone);
      return wall - second;
    } catch {
      return wall;
    }
  };

  const scheduleEl = document.querySelector('script[data-live-schedule]');
  /** @type {any} */
  let schedule = {};
  try {
    schedule = scheduleEl ? JSON.parse(scheduleEl.textContent || '{}') : {};
  } catch {
    schedule = {};
  }

  const start = schedule.date ? zonedToUtc(schedule.date, schedule.time, schedule.timezone || 'America/Chicago') : null;
  const end = start != null ? start + (Number(schedule.duration) || 60) * 60000 : null;

  // Set once this visitor has seen the page live: lives often run past their planned
  // duration, so whoever is on the page keeps the live layout, player and chat until
  // they leave. New visitors after the end get the replay.
  let watchingLive = false;

  /** @returns {'upcoming' | 'live' | 'ended'} */
  const currentStatus = () => {
    if (['upcoming', 'live', 'ended'].includes(schedule.override)) return schedule.override;
    if (start == null || end == null) return 'upcoming';
    const now = Date.now();
    if (now < start) return 'upcoming';
    if (now < end || watchingLive) return 'live';
    return 'ended';
  };

  /* ---------------- Status + countdown ---------------- */

  const labels = schedule.labels || {};
  let lastStatus = '';

  const pad = (n) => String(n).padStart(2, '0');

  const renderCountdown = () => {
    const remaining = start != null ? Math.max(0, start - Date.now()) : 0;
    const total = Math.floor(remaining / 1000);
    const values = {
      days: Math.floor(total / 86400),
      hours: Math.floor((total % 86400) / 3600),
      minutes: Math.floor((total % 3600) / 60),
      seconds: total % 60,
    };
    for (const el of document.querySelectorAll('[data-live-countdown] [data-unit]')) {
      const unit = /** @type {HTMLElement} */ (el).dataset.unit;
      const value = values[/** @type {keyof typeof values} */ (unit)];
      const text = unit === 'days' ? String(value) : pad(value);
      if (el.textContent !== text) el.textContent = text;
    }
  };

  const applyStatus = () => {
    const status = currentStatus();
    if (status === 'live' && !schedule.override) watchingLive = true;
    if (status !== lastStatus) {
      lastStatus = status;
      root.dataset.liveStatus = status;
      for (const pill of document.querySelectorAll('.ard-live__status')) {
        /** @type {HTMLElement} */ (pill).dataset.status = status;
        const label = pill.querySelector('[data-live-status-label]');
        if (label && labels[status]) label.textContent = labels[status];
      }
      document.dispatchEvent(new CustomEvent('ardyss-live:status', { detail: { status } }));
    }
    if (status === 'upcoming') renderCountdown();
  };

  /* ---------------- Local date ---------------- */

  const renderLocalDate = () => {
    if (start == null) return;
    const locale = document.documentElement.lang || undefined;
    const date = new Intl.DateTimeFormat(locale, { weekday: 'long', month: 'long', day: 'numeric' }).format(start);
    const time = new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).format(start);
    for (const el of document.querySelectorAll('[data-live-date]')) el.textContent = date;
    for (const el of document.querySelectorAll('[data-live-time]')) el.textContent = time;
  };

  /* ---------------- Calendar ---------------- */

  /** @param {number} ms */
  const icsStamp = (ms) => new Date(ms).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

  /** @param {string} value */
  const icsEscape = (value) => String(value || '').replace(/\\/g, '\\\\').replace(/([,;])/g, '\\$1').replace(/\r?\n/g, '\\n');

  const setupCalendar = () => {
    if (start == null || end == null) {
      for (const el of document.querySelectorAll('[data-live-calendar]')) el.setAttribute('hidden', '');
      return;
    }
    const title = schedule.title || document.title;
    const details = `${schedule.description || ''}\n\n${location.href.split('#')[0]}`.trim();

    const google = new URL('https://calendar.google.com/calendar/render');
    google.searchParams.set('action', 'TEMPLATE');
    google.searchParams.set('text', title);
    google.searchParams.set('dates', `${icsStamp(start)}/${icsStamp(end)}`);
    google.searchParams.set('details', details);
    google.searchParams.set('location', location.href.split('#')[0]);
    for (const el of document.querySelectorAll('[data-live-calendar="google"]')) {
      /** @type {HTMLAnchorElement} */ (el).href = google.toString();
    }

    for (const el of document.querySelectorAll('[data-live-calendar="ics"]')) {
      el.addEventListener('click', (event) => {
        event.preventDefault();
        const ics = [
          'BEGIN:VCALENDAR',
          'VERSION:2.0',
          'PRODID:-//Ardyss//Ardyss Live//EN',
          'BEGIN:VEVENT',
          `UID:${start}-ardyss-live@${location.hostname}`,
          `DTSTAMP:${icsStamp(Date.now())}`,
          `DTSTART:${icsStamp(start)}`,
          `DTEND:${icsStamp(end)}`,
          `SUMMARY:${icsEscape(title)}`,
          `DESCRIPTION:${icsEscape(details)}`,
          `URL:${location.href.split('#')[0]}`,
          'END:VEVENT',
          'END:VCALENDAR',
        ].join('\r\n');
        const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar;charset=utf-8' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = 'ardyss-live.ics';
        document.body.append(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      });
    }
  };

  /* ---------------- Players (load on demand) ---------------- */

  /**
   * Browsers only allow autoplay without sound, so an automatic start is muted.
   * @param {Element} frame
   */
  const muteEmbeds = (frame) => {
    for (const iframe of frame.querySelectorAll('iframe[src]')) {
      try {
        const url = new URL(iframe.getAttribute('src') || '', location.href);
        if (/youtube/.test(url.hostname)) url.searchParams.set('mute', '1');
        else if (/vimeo/.test(url.hostname)) url.searchParams.set('muted', '1');
        else if (/facebook/.test(url.hostname)) url.searchParams.set('mute', 'true');
        else continue;
        url.searchParams.set('autoplay', /facebook/.test(url.hostname) ? 'true' : '1');
        iframe.setAttribute('src', url.toString());
      } catch {}
    }
  };

  /**
   * @param {HTMLElement} player
   * @param {{ muted?: boolean }} [options]
   */
  const loadPlayer = (player, { muted = false } = {}) => {
    const status = currentStatus();
    const template = /** @type {HTMLTemplateElement | null} */ (
      player.querySelector(`template[data-live-embed~="${status}"]`) || player.querySelector('template[data-live-embed="any"]')
    );
    const frame = player.querySelector('[data-live-frame]');
    if (!template || !frame) return;

    const content = template.content.cloneNode(true);
    if (muted && content instanceof DocumentFragment) muteEmbeds(content);
    frame.replaceChildren(content);
    player.dataset.loadedFor = status;
    if (status === 'live') watchingLive = true;
    // Scripts from embed codes (OneStream and similar) only run when recreated.
    for (const old of frame.querySelectorAll('script')) {
      const script = document.createElement('script');
      for (const attr of old.attributes) script.setAttribute(attr.name, attr.value);
      script.textContent = old.textContent;
      old.replaceWith(script);
    }
    player.dataset.loaded = 'true';
  };

  // "Play automatically while live": start the live player without waiting for a click.
  const autoplayLive = () => {
    if (currentStatus() !== 'live') return;
    for (const player of document.querySelectorAll('[data-live-player][data-autoplay-live="true"]:not([data-loaded="true"])')) {
      loadPlayer(/** @type {HTMLElement} */ (player), { muted: true });
    }
  };

  const setupPlayers = () => {
    for (const player of document.querySelectorAll('[data-live-player]')) {
      const el = /** @type {HTMLElement} */ (player);
      for (const play of el.querySelectorAll('[data-live-play]')) play.addEventListener('click', () => loadPlayer(el));
      if (el.dataset.autoload === 'true') loadPlayer(el);
    }
    autoplayLive();
    // A loaded player only resets when the status really needs another source
    // (poster -> live, or live -> replay for visitors who weren't watching).
    document.addEventListener('ardyss-live:status', (event) => {
      const status = /** @type {CustomEvent} */ (event).detail?.status;
      for (const player of document.querySelectorAll('[data-live-player][data-loaded="true"]')) {
        const el = /** @type {HTMLElement} */ (player);
        if (el.dataset.loadedFor === status) continue;
        delete el.dataset.loaded;
        delete el.dataset.loadedFor;
        el.querySelector('[data-live-frame]')?.replaceChildren();
      }
      autoplayLive();
    });
  };

  /* ---------------- Live header (title and description from the banner) ---------------- */

  const renderLiveCopy = () => {
    if (schedule.title) for (const el of document.querySelectorAll('[data-live-title]')) el.textContent = schedule.title;
    for (const el of document.querySelectorAll('[data-live-description]')) el.textContent = schedule.description || '';

    for (const el of document.querySelectorAll('[data-live-kicker]')) {
      el.textContent = schedule.kicker || '';
      el.toggleAttribute('hidden', !schedule.kicker);
    }

    const host = schedule.host || {};
    for (const el of document.querySelectorAll('[data-live-host]')) {
      el.toggleAttribute('hidden', !host.name);
      if (!host.name) continue;
      const text = el.querySelector('[data-live-host-text]');
      if (text) text.textContent = [host.prefix, host.name].filter(Boolean).join(' ');
      const image = /** @type {HTMLImageElement | null} */ (el.querySelector('[data-live-host-image]'));
      if (image && host.image) {
        image.src = host.image;
        image.alt = host.name;
        image.removeAttribute('hidden');
      }
    }
  };

  /* ---------------- Side panel tabs (featured products / chat) ---------------- */

  /**
   * @param {HTMLElement} panel
   * @param {string} tab
   */
  const selectTab = (panel, tab) => {
    if (!panel.querySelector(`[data-live-pane="${tab}"]`)) return;
    panel.dataset.activeTab = tab;
    for (const pane of panel.querySelectorAll('[data-live-pane]')) {
      pane.toggleAttribute('data-live-pane-active', /** @type {HTMLElement} */ (pane).dataset.livePane === tab);
    }
    for (const button of panel.querySelectorAll('[data-live-tab]')) {
      button.setAttribute('aria-selected', String(/** @type {HTMLElement} */ (button).dataset.liveTab === tab));
    }
  };

  const setupTabs = () => {
    const panels = /** @type {HTMLElement[]} */ ([...document.querySelectorAll('[data-live-tabs]')]);
    const sync = () => {
      for (const panel of panels) {
        // Chat only exists while live; otherwise the products are the only pane.
        const tab = currentStatus() === 'live' ? panel.dataset.defaultTab || 'products' : 'products';
        selectTab(panel, panel.querySelector(`[data-live-pane="${tab}"]`) ? tab : 'chat');
      }
    };
    for (const panel of panels) {
      for (const button of panel.querySelectorAll('[data-live-tab]')) {
        button.addEventListener('click', () => selectTab(panel, /** @type {HTMLElement} */ (button).dataset.liveTab || 'products'));
      }
    }
    sync();
    document.addEventListener('ardyss-live:status', sync);
  };

  /* ---------------- Theme editor: show the schedule in the editor's local time ---------------- */

  const renderEditorNote = () => {
    const note = document.querySelector('[data-live-editor-note]');
    if (!note) return;
    if (start == null || end == null) {
      note.textContent = 'Agrega la fecha (AAAA-MM-DD) y la hora (HH:MM) del live.';
    } else {
      const fmt = new Intl.DateTimeFormat('es-MX', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
      const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      note.textContent = `En tu hora local (${zone}) el live inicia ${fmt.format(start)} y termina ${fmt.format(end)}. Si no coincide, revisa la zona horaria del live.`;
    }
    note.removeAttribute('hidden');
  };

  /* ---------------- Scroll helpers ---------------- */

  const targets = {
    stream: () => document.getElementById('ard-live-stream'),
    products: () => document.querySelector('[data-live-target="products"], [id$="__live_products"]'),
    past: () => document.getElementById('ard-live-past'),
  };

  const setupScroll = () => {
    document.addEventListener('click', (event) => {
      const trigger = /** @type {HTMLElement | null} */ (
        event.target instanceof Element ? event.target.closest('[data-live-scroll]') : null
      );
      if (!trigger) return;
      const getTarget = targets[/** @type {keyof typeof targets} */ (trigger.dataset.liveScroll)];
      const target = getTarget?.();
      if (!target) return;
      event.preventDefault();
      const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
    });
  };

  /* ---------------- SEO: Event structured data ---------------- */

  // Built here because only the browser can turn date + time zone into an exact instant.
  const injectEventSchema = () => {
    if (start == null || end == null || schedule.seo === false || document.querySelector('script[data-live-jsonld]')) return;
    const pageUrl = location.href.split('#')[0].split('?')[0];
    const status = currentStatus();
    const data = {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: schedule.title || document.title,
      description: schedule.description || undefined,
      image: schedule.image ? [schedule.image] : undefined,
      startDate: new Date(start).toISOString(),
      endDate: new Date(end).toISOString(),
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
      location: { '@type': 'VirtualLocation', url: pageUrl },
      organizer: { '@type': 'Organization', name: schedule.organizer, url: schedule.organizerUrl },
      isAccessibleForFree: true,
      ...(status === 'live' ? { subEvent: { '@type': 'BroadcastEvent', isLiveBroadcast: true, startDate: new Date(start).toISOString() } } : {}),
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.liveJsonld = '';
    script.textContent = JSON.stringify(data);
    document.head.append(script);
  };

  const setupCalendarMenu = () => {
    document.addEventListener('click', (event) => {
      for (const menu of document.querySelectorAll('details[data-live-calendar-menu][open]')) {
        if (!(event.target instanceof Node) || !menu.contains(event.target)) menu.removeAttribute('open');
      }
    });
  };

  /* ---------------- Boot ---------------- */

  const boot = () => {
    applyStatus();
    renderLiveCopy();
    renderLocalDate();
    renderEditorNote();
    setupTabs();
    setupCalendar();
    setupCalendarMenu();
    injectEventSchema();
    setupPlayers();
    setupScroll();
    window.setInterval(applyStatus, 1000);

    // Arriving from a banner on another page (e.g. /pages/ardyss-live#live-products).
    if (location.hash === '#live-products') {
      requestAnimationFrame(() => targets.products()?.scrollIntoView({ block: 'start' }));
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
